import { CreateElement } from "vue";
import store from "@/store";
import _ from "@/boot/lodash";
import i18n from "@/i18n";
import { DataModules } from "@/store/modules/data";
import { DefaultFilters as ProductCategoriesFilters } from "@/data/filters/productCategories";
import { DefaultSorters as ProductCategoriesSorters } from "@/data/sorters/productCategories";
import { IImportMappingSource } from "@/models/imports";
import { IProductCategory } from "@/models/products";
import ProductImage from "@/components/app/global/products/productImage.vue";
import { ProductTypes } from "@/data/enums/products";

const productCategoryRoute = (productCategory: IProductCategory) => {
  return {
    name: "adminCatalogueCategory",
    params: {
      categoryId: productCategory.id
    }
  };
};

const rootCategoriesScope = () => {
  const brandId = store.getters["brand/id"];
  return store.getters[
    `data/${DataModules.CATALOGUE_CATEGORIES}/rootCategoriesScope`
  ](brandId, [ProductTypes.SINGLE_PRODUCT]);
};

const rootCategories = () => {
  return store.getters[`data/listArray`]({
    storeModule: DataModules.CATALOGUE_CATEGORIES,
    scope: rootCategoriesScope()
  });
};

const getCategoryFullpath = (
  h: CreateElement,
  parentCategories: IProductCategory[]
) => {
  return h(
    "span",
    {
      class: "has-text-grey"
    },
    _.concat(
      _.map(parentCategories, parentCategory => {
        return [
          h("span", {}, parentCategory.name),
          h("span", { class: "has-margin-x-125" }, "/")
        ];
      })
    )
  );
};

const firstLabel = (
  h: CreateElement,
  productCategory: IProductCategory,
  onClick: Function = () => ({})
) => {
  const parentCategories = store.getters[
    `data/${DataModules.CATALOGUE_CATEGORIES}/getFullCategoryPath`
  ]({
    categories: rootCategories(),
    categoryId: productCategory.id,
    showTargetCategory: false
  });

  return h(
    "div",
    {
      style: {
        position: "relative",
        height: "1.5em",
        display: "flex",
        "align-tems": "center"
      }
    },
    [
      h(
        "div",
        {
          style: {
            display: "flex",
            "align-items": "center",
            width: "100%",
            position: "absolute"
          }
        },
        [
          h(
            "p",
            { class: "has-overflow-ellipsis-left" },
            _.concat(
              parentCategories.length
                ? [getCategoryFullpath(h, parentCategories)]
                : [],
              [
                h(
                  "u-link",
                  {
                    props: { to: productCategoryRoute(productCategory) },
                    on: { click: onClick }
                  },
                  [h("strong", {}, productCategory.name)]
                )
              ]
            )
          )
        ]
      )
    ]
  );
};

const secondLabel = (h: CreateElement, productCategory: IProductCategory) => {
  return h("p", { class: "is-size-8" }, [
    h(
      "span",
      {
        class: "has-text-grey-light"
      },
      `${i18n.tc(
        "_plurals.products",
        productCategory.products_count
      )} • ${i18n.tc(
        "_plurals.subcategories",
        productCategory.subcategories_count
      )}`
    )
  ]);
};

const media = (h: CreateElement, productCategory: IProductCategory) => {
  const mediaSrc = productCategory.image?.full_url;
  return mediaSrc
    ? h("u-square-image", { props: { src: mediaSrc } }, [])
    : h(ProductImage, { props: { product: {} } }, []);
};

export const ProductCategorySource: IImportMappingSource = {
  props: {
    availableFilters: ProductCategoriesFilters,
    availableSorters: ProductCategoriesSorters(),
    usedDataModule: DataModules.CATALOGUE_MAIN_PRODUCT_CATEGORIES,
    guardFunctionalities: ["base_list_product_categories"],
    storeParams: querySearch => {
      return {
        with: ["image"].join(),
        ...(querySearch ? { "filter[name|like]": `%${querySearch}%` } : {})
      };
    },
    loadExtraData: vm => {
      return store.dispatch(`data/${DataModules.CATALOGUE_CATEGORIES}/list`, {
        productTypes: [ProductTypes.SINGLE_PRODUCT],
        scope: rootCategoriesScope(),
        params: {
          order: "order",
          limit: 0,
          "filter[level]": 1
        },
        vm,
        ignoreStored: true
      });
    },
    destroyExtraData: vm => {
      store.dispatch("data/bin", {
        storeModule: DataModules.CATALOGUE_CATEGORIES,
        scope: rootCategoriesScope(),
        vm
      });
    }
  },
  render: {
    id: (productCategory: IProductCategory) => productCategory.id,
    firstLabel: (
      h: CreateElement,
      productCategory: IProductCategory,
      onClick: Function
    ) => firstLabel(h, productCategory, onClick),
    secondLabel: (h: CreateElement, productCategory: IProductCategory) =>
      secondLabel(h, productCategory),
    media: (h: CreateElement, productCategory: IProductCategory) =>
      media(h, productCategory)
  }
};
